/// <amd-module name="Artifacts/Maven/services/MavenDataService" />
import * as Contracts from "Artifacts/Maven/Contracts/Maven";
import * as Res from "Artifacts/Maven/Resources";
import { getMavenClient, IMavenRestClient } from "Artifacts/Maven/RestClient/Maven";
import { BatchExecutor, IBatchIterator } from "Artifacts/Package/services/BatchHandling";
import { UpstreamingBehavior } from "Artifacts/Root/types/PackagingShared";
import { IVssService, Services, VssService } from "VSS/Platform/Context";

export interface IFeedMavenDataService extends IVssService {
  readonly batchSize: number;
  batchDeletePackageVersions(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void>;
  batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void>;
  batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void>;
  batchPromotePackages(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void[]>;
  getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior>;
  setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId?: string,
  ): Promise<void>;
}

class MavenDataService extends VssService implements IFeedMavenDataService {
  get MavenRestClient(): IMavenRestClient {
    return getMavenClient(this.pageContext);
  }

  get batchSize() {
    return 10;
  }

  public async batchDeletePackageVersions(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: Contracts.MavenMinimalPackageDetails[]) => {
        const batchRequest = <Contracts.MavenPackagesBatchRequest>{
          data: null,
          operation: Contracts.MavenBatchOperationType.Delete,
          packages: packageDetails,
        };
        await this.MavenRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public async batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: Contracts.MavenMinimalPackageDetails[]) => {
        const batchRequest = <Contracts.MavenPackagesBatchRequest>{
          data: null,
          operation: Contracts.MavenBatchOperationType.RestoreToFeed,
          packages: packageDetails,
        };
        await this.MavenRestClient.updateRecycleBinPackages(batchRequest, feedId, projectId);
      },
    );
  }

  public async batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: Contracts.MavenMinimalPackageDetails[]) => {
        const batchRequest = <Contracts.MavenPackagesBatchRequest>{
          data: null,
          operation: Contracts.MavenBatchOperationType.PermanentDelete,
          packages: packageDetails,
        };
        await this.MavenRestClient.updateRecycleBinPackages(batchRequest, feedId, projectId);
      },
    );
  }

  public batchPromotePackages(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>,
  ): Promise<void[]> {
    return BatchExecutor.executeInParallel(iterator, async (packageDetails) => {
      const batchRequest = <Contracts.MavenPackagesBatchRequest>{
        data: { viewId },
        operation: Contracts.MavenBatchOperationType.Promote,
        packages: packageDetails,
      };
      await this.MavenRestClient.updatePackageVersions(batchRequest, feedId, projectId);
    });
  }

  public getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior> {
    const nameParts = this.parseName(packageName);
    return this.MavenRestClient.getUpstreamingBehavior(
      feedId,
      nameParts[0],
      nameParts[1],
      projectId,
    );
  }

  public setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId?: string,
  ): Promise<void> {
    const nameParts = this.parseName(packageName);
    return this.MavenRestClient.setUpstreamingBehavior(
      feedId,
      nameParts[0],
      nameParts[1],
      behavior,
      projectId,
    );
  }

  private parseName(packageName: string): string[] {
    // Maven packages are of the form groupId:artifactId. Anything other than that are rejected.
    const nameParts = (packageName || "").split(":");
    if (nameParts.length !== 2) {
      throw new Error(Res.MavenOverview_InvalidPackageError);
    }
    return nameParts;
  }
}

Services.add("IFeedMavenDataService", { serviceFactory: MavenDataService });
